import Edit from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ExtractionStatus, ManifestDocumentsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { ManifestReviewDialog } from "../../Customer/Waste/Manifests/ManifestReviewDialog";
import { MANIFEST_DOCUMENTS_QUERY } from "./api";
import { ReParseDialog } from "./ReParseDialog";

type ManifestDocumentsProps = {
  tenantId: string;
};

type Row = ManifestDocumentsQuery["documents"]["items"][number];

type ExtractionStatusDisplay = {
  label: string;
  color: "warning" | "info" | "success" | "default";
};

const EXTRACTION_STATUS_DISPLAY: Record<ExtractionStatus, ExtractionStatusDisplay> = {
  [ExtractionStatus.Failed]: { label: "Failed", color: "warning" },
  [ExtractionStatus.Pending]: { label: "Processing", color: "info" },
  [ExtractionStatus.Succeeded]: { label: "Succeeded", color: "success" },
  [ExtractionStatus.NotAttempted]: { label: "Not started", color: "default" },
};

/**
 * Staff-only diagnostic page that lists every RcraManifest document in the
 * tenant alongside the manifest record it ended up linked to (if any). Helps
 * identify orphaned documents and documents that landed on the wrong manifest.
 */
export function ManifestDocuments({ tenantId }: ManifestDocumentsProps) {
  const theme = useTheme();

  // Row to re-parse. Null when the dialog is closed. The dialog itself owns
  // the override field state; this is just the open/closed signal + which row.
  const [pendingReParseRow, setPendingReParseRow] = useState<Row | null>(null);
  // Documents to open in the manifest-specific review dialog. Used both for
  // direct edits (Edit action) and for the post-re-parse review handoff —
  // the analyzing dialog otherwise falls back to the generic Document AI flow.
  const [reviewDocumentIds, setReviewDocumentIds] = useState<string[] | null>(null);
  // Per-row overflow menu anchor (Re-parse lives here as a secondary action).
  // Two states instead of an object so each updates with one setter call.
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [actionsAnchorRow, setActionsAnchorRow] = useState<Row | null>(null);
  const closeActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
    setActionsAnchorRow(null);
  }, []);

  const columns = useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) =>
          tenantId ? (
            <MuiLink
              href={`/o/${tenantId}/documents/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <Typography variant="body2" noWrap>
                {row.title}
              </Typography>
              <OpenInNew fontSize="inherit" />
            </MuiLink>
          ) : (
            <Typography variant="body2">{row.title}</Typography>
          ),
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.7,
        minWidth: 130,
        valueGetter: (_, row) => row.createdAt,
        renderCell: ({ row }) => (row.createdAt ? getUtcDisplayDate(row.createdAt, "med") : "—"),
      },
      {
        field: "extractionStatus",
        headerName: "Parse status",
        flex: 0.6,
        minWidth: 120,
        renderCell: ({ row }) => {
          const status = row.extractionStatus ?? ExtractionStatus.NotAttempted;
          const display = EXTRACTION_STATUS_DISPLAY[status];
          const errorTitle = row.extractionError ?? undefined;
          return (
            <Tooltip title={errorTitle ?? ""}>
              <Chip label={display.label} color={display.color} size="small" variant="outlined" />
            </Tooltip>
          );
        },
      },
      {
        field: "extractedGeneratorId",
        headerName: "Generator ID",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.generatorId ?? "",
        renderCell: ({ row }) => {
          const id = row.sourceWasteManifest?.generatorId;
          return id ? (
            <Typography variant="body2">{id}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "linkedFacility",
        headerName: "Linked facility",
        flex: 0.9,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.facility?.name ?? "",
        renderCell: ({ row }) => {
          const facility = row.sourceProcessedManifest?.facility;
          return facility ? (
            <Typography variant="body2" noWrap>
              {facility.name}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "linkedManifest",
        headerName: "Linked manifest",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.manifestTrackingNumber ?? "",
        renderCell: ({ row }) => {
          const linked = row.sourceProcessedManifest;
          if (!linked || !tenantId) {
            return (
              <Typography
                variant="body2"
                color={theme.palette.warning.main}
                sx={{ fontStyle: "italic" }}
              >
                Not linked
              </Typography>
            );
          }
          return (
            <MuiLink
              component={RouterLink}
              to={`/o/${tenantId}/waste/manifests/${linked.id}`}
              variant="body2"
            >
              {linked.manifestTrackingNumber}
            </MuiLink>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        minWidth: 96,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit manifest fields">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setReviewDocumentIds([row.id])}
                  aria-label="Edit manifest fields"
                  disabled={!row.sourceWasteManifest?.id}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  setActionsAnchorEl(e.currentTarget);
                  setActionsAnchorRow(row);
                }}
                aria-label="More actions"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [tenantId, theme]
  );

  const defaultSearch = useMemo(
    () => (tenantId ? `tenantId:${tenantId} documentType:RCRAManifest` : ""),
    [tenantId]
  );

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Lists every RCRA manifest document in this tenant and the manifest record it ended up
          linked to. Use this to spot orphaned uploads or documents that landed on the wrong
          manifest.
        </Typography>
      </Box>

      <OmnisearchDataGrid
        persistenceKey="waste-manifest-documents"
        isURLDriven={false}
        columns={columns}
        dataQuery={MANIFEST_DOCUMENTS_QUERY}
        getItems={(data) => data.documents.items}
        getCount={(data) => data.documents.count}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        noDataMessage="No manifest documents in this tenant."
        rowHeight="auto"
        withPadding={false}
      />

      <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={closeActionsMenu}>
        <MenuItem
          onClick={() => {
            const row = actionsAnchorRow;
            closeActionsMenu();
            if (row) setPendingReParseRow(row);
          }}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-parse</ListItemText>
        </MenuItem>
      </Menu>

      {pendingReParseRow && (
        <ReParseDialog
          key={pendingReParseRow.id}
          row={pendingReParseRow}
          tenantId={tenantId}
          onClose={() => setPendingReParseRow(null)}
          onReadyForReview={setReviewDocumentIds}
        />
      )}

      {reviewDocumentIds && (
        <ManifestReviewDialog
          onClose={() => setReviewDocumentIds(null)}
          documentIds={reviewDocumentIds}
        />
      )}
    </>
  );
}
